import { FC, useEffect, useState } from 'react';
import { RadioButton, RadioButtonGroup } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import type { JobType, StartProcessResponse } from './types';
import { LaunchProcessModal } from './launch-process-modal';
import styles from './page.module.scss';

// Launches Phase 2 (candidate generation). On success the caller navigates to
// the new run's detail page, where progress and review live.
export const GenerateModal: FC<{
  open: boolean;
  onClose: () => void;
  onStarted: (proc: StartProcessResponse) => void;
}> = ({ open, onClose, onStarted }) => {
  const [jobType, setJobType] = useState<JobType>('all');

  // Re-default on every open — the modal stays mounted while closed, so a
  // selection from an abandoned open would otherwise leak into the next one.
  useEffect(() => {
    if (open) setJobType('all');
  }, [open]);

  return (
    <LaunchProcessModal
      open={open}
      heading="Generate Candidates"
      primaryButtonText="Generate Candidates"
      url="/admin/bulk_dedupe/cluster"
      limitLabel="Batch size (optional — cap the number of sub-clusters; leave blank to find all)"
      buildBody={(limit) => ({
        jobType,
        ...(limit === '' ? {} : { limit }),
      })}
      onClose={onClose}
      onStarted={onStarted}
    >
      <p>
        Groups same-name entities linked by a concordant address or a confirmed
        alias into candidate sub-clusters for review. Addresses must be
        normalized first.
      </p>
      <RadioButtonGroup
        legendText="Job type"
        name="job-type"
        orientation="vertical"
        valueSelected={jobType}
        onChange={(v) => setJobType(v as JobType)}
      >
        <RadioButton
          labelText="Imported → Imported"
          value="imported_imported"
          id="jt-a"
        />
        <RadioButton
          labelText={
            <span className={styles.labelWithTooltip}>
              All{' '}
              <TooltipIcon
                definition="Every same-name merge type: Imported → Imported, Imported → Individual, Imported → Org, Individual → Individual, and Org → Org. (A cluster mixing Individual and Org is still excluded.)"
                autoAlign
              >
                <Information />
              </TooltipIcon>
            </span>
          }
          value="all"
          id="jt-b"
        />
      </RadioButtonGroup>
    </LaunchProcessModal>
  );
};
